import React, { useCallback, useEffect, useState } from 'react';

import { useQueryClient } from 'react-query';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { processQuestion } from './helpers';
import useCreateMessage from './hooks/useCreateMessage';
import useCreateThread from './hooks/useCreateThread';
import ManualAnswerModal from './ManualAnswer/ManualAnswerModal';
import Question from './Question';
import ThreadView from './ThreadView';
import { PageName, TrackingEvents } from '@/analytics/events';
import { trackCustomEvent, trackPageView } from '@/analytics/Mixpanel';
import { useGetDocIds } from '@/api/queries/useGetDocIds';
import { QueryEntity } from '@/api/types';
import { DraftType, ExecuteKimSearch, KimMode } from '@/types';

const CaseAssistant = React.memo(() => {
  const [questionValue, setQuestionValue] = useState('');
  const [draftType, setDraftType] = useState<DraftType | null>(null);
  const [mode, setMode] = useState<KimMode>(KimMode.QuestionFlow);
  const [taggedDocuments, setTaggedDocuments] = useState<{ doc_id: string; file_name: string }[]>([]);

  const [displayQuestion, setDisplayQuestion] = useState('');
  const [isCreatingThread, setIsCreatingThread] = useState(false);
  const [processingThreadIds, setProcessingThreadIds] = useState<string[]>([]);

  const [isManualAnswerModalOpen, setIsManualAnswerModalOpen] = useState(false);

  const queryClient = useQueryClient();

  const resetState = () => {
    setQuestionValue('');
    setTaggedDocuments([]);
    setMode(KimMode.QuestionFlow);
    setDraftType(null);
  };

  const resetStateOnRequestComplete = (threadId?: string) => {
    // remove threadId from processing threads when complete
    if (threadId) {
      setProcessingThreadIds((prev) => prev.filter((id) => id !== threadId));
    }
    setIsCreatingThread(false);
    setDisplayQuestion('');
    // Refetch the user
    queryClient.invalidateQueries({ queryKey: [QueryEntity.User] });
  };

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const caseId = searchParams.get('caseId');
  const threadId = searchParams.get('threadId');

  const { data: responseDocIds } = useGetDocIds(caseId || '');

  // Mutations
  const { mutate: createThread } = useCreateThread(caseId || '');
  const { mutate: createMessage } = useCreateMessage();

  // Thread navigation handlers
  const handleNewThread = () => {
    searchParams.delete('threadId');
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  useEffect(() => {
    setDraftType(null);
  }, [mode]);

  const goToThread = (threadId: string) => {
    // If a document is selected in params, delete it
    searchParams.delete('selectedDocId');

    searchParams.set('threadId', threadId);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  // Error handling
  const errorToast = () => {
    toast.error('Error processing your question. Please try again.');
  };

  const executeSearch: ExecuteKimSearch = ({ question: value, draftType, questionMode }) => {
    if (value.trim() === '') {
      return;
    }
    setIsCreatingThread(true);
    const question = processQuestion(value);

    // First, create the thread and add the message to the thread
    createThread(question, {
      onSuccess: (data) => {
        // Add this thread to the processing array
        setProcessingThreadIds((prev) => [...prev, data.id]);

        // Set the thread id in search params
        searchParams.set('threadId', data.id);
        navigate(`${location.pathname}?${searchParams.toString()}`);

        const messagePayload = {
          caseId: caseId || '',
          question: question,
          docIds: taggedDocuments.map((doc) => doc.doc_id),
          draftType,
          mode: questionMode,
          threadId: data.id,
        };
        setDisplayQuestion(question);
        resetState();
        trackCustomEvent(TrackingEvents.AskedKimQuestion, { caseId, mode });

        // Then create the message
        createMessage(messagePayload, {
          onError: (error) => {
            console.error('Error creating message');
            errorToast();
          },
          onSettled: () => {
            resetStateOnRequestComplete(data.id);
          },
        });
      },
      onError: (error) => {
        console.error('Error creating thread');
        errorToast();
        resetStateOnRequestComplete();
      },
    });
  };

  useEffect(() => {
    const { question, mode } = location.state || {};
    if (question && mode) {
      setQuestionValue(question);
      setMode(mode);
      executeSearch({ question: question, draftType: null, questionMode: mode });
    }
  }, [location.state]);

  useEffect(() => {
    trackPageView(PageName.Kim);
  }, []);

  return (
    <div className="flex h-full w-full gap-2 border-b">
      <>
        <div className="flex h-full w-full flex-col">
          {threadId ? (
            <ThreadView
              question={displayQuestion}
              caseId={caseId || ''}
              threadId={threadId || ''}
              processingThreadIds={processingThreadIds}
              errorToast={errorToast}
              executeSearch={executeSearch}
              // Previous thread management
              goToThread={goToThread}
              handleNewThread={handleNewThread}
            />
          ) : (
            <Question
              caseId={caseId ?? ''}
              // Question Execution
              executeSearch={executeSearch}
              setQuestionValue={setQuestionValue}
              questionValue={questionValue}
              isCreatingThread={isCreatingThread}
              draftType={draftType}
              setDraftType={setDraftType}
              // Document @-ing
              docs={responseDocIds?.documents ?? []}
              taggedDocuments={taggedDocuments}
              setTaggedDocuments={setTaggedDocuments}
              // Flow
              mode={mode}
              setMode={setMode}
              // Threads
              goToThread={goToThread}
              onStartManualAnswer={() => setIsManualAnswerModalOpen(true)}
            />
          )}
        </div>

        {/* Manual Answer Modal */}
        <ManualAnswerModal
          caseId={caseId ?? ''}
          isOpen={isManualAnswerModalOpen}
          onClose={() => setIsManualAnswerModalOpen(false)}
          onComplete={(threadId) => {
            setIsManualAnswerModalOpen(false);
            goToThread(threadId);
          }}
        />
      </>
    </div>
  );
});

export default CaseAssistant;
